import React, { useState } from 'react';
import { Container, Box, Typography, TextField, Button, Link, MenuItem } from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useAuth0 } from '@auth0/auth0-react';
import "../styles/search.css";

const Search = () => {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();

  const [phone, setPhone] = useState('');
  const [zip, setZip] = useState('');
  const [stateAbbr, setStateAbbr] = useState('');
  const handleZipChange = (e) => {
    // keep only digits, cap at 5
    const digits = e.target.value.replace(/\D/g, '').slice(0, 5);
    setZip(digits);
  };

  const handleStateChange = (e) => {
    // keep letters only, uppercase, cap at 2
    const val = e.target.value.replace(/[^a-zA-Z]/g, '').toUpperCase().slice(0, 2);
    setStateAbbr(val);
  };

  async function searchPatients(filters) {
    const token = await getAccessTokenSilently({ authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE } })
    const params = new URLSearchParams();

    // Only add fields that exist
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value.trim() !== "") {
        params.append(key, value);
      }
    });

    const response = await fetch(`${process.env.REACT_APP_API_BASE_URI}/api/patientsSearch?${params.toString()}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || "Search failed");
    }

    return await response.json();
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    const form = new FormData(e.currentTarget);

    const filters = {
      first_name: form.get("first_name") || "",
      middle_name: form.get("middle_name") || "",
      last_name: form.get("last_name") || "",
      dob: form.get("dob") || "",
      street_address: form.get("street_address") || "",
      city: form.get("city") || "",
      state: stateAbbr || form.get("state") || "",
      zip: zip || form.get("zip") || "",
      phone: phone || "",
      sex: form.get("sex") || "",
      race: form.get("race") || "",
      highest_education_level: form.get("highest_education_level") || "",
    };

    try {
      const rows = await searchPatients(filters);
      navigate('/searchResults', {
        state: {
          results: rows,
          filters,
        },
      });

    } catch (err) {
      console.error(err);
      const message = err?.message || "Search failed";
      if (message === "No patient access permissions") {
        alert("No patient access permissions. Contact the system administrator.");
        return;
      }
      alert(message);
    }
  };


  return (
    <Container maxWidth="xs" className="search-container">
      <Box className="search-top-link-row">
        <Link
          component={RouterLink}
          to="/testSearch"
          underline="hover"
          color="primary"
          className="search-switch-link"
        >
          Switch to Test Search
        </Link>
      </Box>


      <Box className="search-form-wrapper">
        <Typography component="h1" variant="h5">
          Patient Search
        </Typography>

        <Box component="form" autoComplete="off" onSubmit={handleSubmit} className="search-form">
          <TextField
            margin="normal"
            fullWidth
            label="First Name"
            name="first_name"
            data-cy="first_name-cy"
            autoFocus
          />
          <TextField
            margin="normal"
            fullWidth
            label="Last Name"
            name="last_name"
            data-cy="last_name-cy"
          />

          <TextField
            margin="normal"
            fullWidth
            label="Date of Birth"
            name="dob"
            type="date"
            data-cy="date_of_birth-cy"
            inputProps={{
              max: new Date().toISOString().split('T')[0], // disables future dates
            }}
            InputLabelProps={{ shrink: true }}
          />

          <TextField select fullWidth label="Sex" name="sex" defaultValue="" margin="normal">
            <MenuItem value="">Any</MenuItem>
            <MenuItem value="1">Male</MenuItem>
            <MenuItem value="2">Female</MenuItem>
            <MenuItem value="3">Trans male</MenuItem>
            <MenuItem value="4">Trans female</MenuItem>
          </TextField>

          <TextField select fullWidth label="Race / Ethnicity" name="race" defaultValue="" margin="normal">
            <MenuItem value="">Any</MenuItem>
            <MenuItem value="1">White</MenuItem>
            <MenuItem value="2">Black/African-American</MenuItem>
            <MenuItem value="3">Hispanic/Latin-American</MenuItem>
            <MenuItem value="4">Native/Indigenous</MenuItem>
            <MenuItem value="5">Asian</MenuItem>
            <MenuItem value="6">Native Hawaiian/Pacific Islander</MenuItem>
          </TextField>

          <TextField select fullWidth label="Education Level" name="highest_education_level" defaultValue="" margin="normal">
            <MenuItem value="">Any</MenuItem>
            <MenuItem value="1">Less than high school diploma</MenuItem>
            <MenuItem value="2">High School diploma/GED</MenuItem>
            <MenuItem value="3">Some college or trade school</MenuItem>
            <MenuItem value="4">Associate's degree</MenuItem>
            <MenuItem value="5">Bachelor's degree</MenuItem>
            <MenuItem value="6">Graduate/professional degree</MenuItem>
          </TextField>


          <Box className="search-location-row">
            <TextField
              label="City"
              name="city"
              data-cy="city-cy"
              className="search-city-field"
            />

            <TextField
              label="State"
              name="state"
              data-cy="state-cy"
              value={stateAbbr}
              onChange={handleStateChange}
              inputProps={{ maxLength: 2 }}
              className="search-state-field"
            />

            <TextField
              label="ZIP"
              name="zip"
              data-cy="zipcode-cy"
              value={zip}
              onChange={handleZipChange}
              inputMode="numeric"
              inputProps={{ maxLength: 5, pattern: '\\d{5}' }}
              className="search-zip-field"
              helperText={zip && zip.length < 5 ? 'ZIP must be 5 digits' : ' '}
            />
          </Box>

          <Box className="search-phone-row" data-cy="phone_number-cy">
            <PhoneInput
              country={'us'}
              value={phone}
              onChange={setPhone}
              containerClass="search-phone-container"
              inputClass="search-phone-input"
            />
          </Box>

          <Button variant="contained" className="search-submit-btn" data-cy="search_submit_btn" type="submit">
            Search
          </Button>
        </Box>
      </Box>
    </Container>
  );
}

export default Search;